/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { MessageType } from 'components/Messages';
import { useAppDispatch } from 'hooks/redux';
import { useCallback } from 'react';
import type { RootState } from 'redux/store';
import type { FileQueue, IChatStore } from 'types/chat';

export const initialState: IChatStore = {
  selectedCannedMessages: {},
  roomTagNames: [],
  fileQueue: [],
  replyingToMessage: null,
};

const chatSlice = createSlice({
  name: 'chatSlice',
  initialState,
  reducers: {
    setSelectedCannedMessages: (state, action: PayloadAction<IChatStore['selectedCannedMessages']>) => {
      state.selectedCannedMessages = action.payload;
    },
    setRoomTagNames: (state, action: PayloadAction<IChatStore['roomTagNames']>) => {
      state.roomTagNames = action.payload;
    },
    addFileQueue: (state, action: PayloadAction<FileQueue>) => {
      state.fileQueue.push(action.payload);
    },
    updateFileQueue: (state, action: PayloadAction<FileQueue>) => {
      state.fileQueue = state.fileQueue.map((file) =>
        file.name === action.payload.name ? { ...file, ...action.payload } : file
      );
    },
    removeFileQueue: (state, action: PayloadAction<string>) => {
      state.fileQueue = state.fileQueue.filter((file) => file.name !== action.payload);
    },
    setReplyingToMessage: (state, action: PayloadAction<MessageType>) => {
      state.replyingToMessage = action.payload;
    },
    clearReplyingToMessage: (state) => {
      state.replyingToMessage = null;
    },
    resetChat: () => initialState,
  },
});

export const { setSelectedCannedMessages, setRoomTagNames, setReplyingToMessage, clearReplyingToMessage, resetChat } =
  chatSlice.actions;

/** Selects the message the agent is currently replying to (null when idle). */
export const selectReplyingToMessage = (state: RootState) => state.chat.replyingToMessage;

export function useChatStore() {
  const dispatch = useAppDispatch();
  return {
    setSelectedCannedMessages: useCallback(
      (cannedMessage: IChatStore['selectedCannedMessages']) => dispatch(setSelectedCannedMessages(cannedMessage)),
      [dispatch]
    ),
    setRoomTagNames: useCallback(
      (tagNames: IChatStore['roomTagNames']) => dispatch(setRoomTagNames(tagNames)),
      [dispatch]
    ),
    addFileQueue: useCallback((file: FileQueue) => dispatch(chatSlice.actions.addFileQueue(file)), [dispatch]),
    updateFileQueue: useCallback((file: FileQueue) => dispatch(chatSlice.actions.updateFileQueue(file)), [dispatch]),
    removeFileQueue: useCallback(
      (fileName: string) => dispatch(chatSlice.actions.removeFileQueue(fileName)),
      [dispatch]
    ),
    setReplyingToMessage: useCallback((message: MessageType) => dispatch(setReplyingToMessage(message)), [dispatch]),
    clearReplyingToMessage: useCallback(() => dispatch(clearReplyingToMessage()), [dispatch]),
    resetChat: useCallback(() => dispatch(resetChat()), [dispatch]),
  };
}

export default chatSlice.reducer;
